// src/utils/authFetch.js
// Drop-in fetch wrapper that automatically refreshes an expired JWT.
//
// Usage:
//   import { authFetch } from '../utils/authFetch';
//   const res = await authFetch('/api/whatever', { method: 'POST', body: JSON.stringify(...) });
//
// Behavior:
//   1. Reads the JWT from localStorage('token') and sends it as Authorization: Bearer.
//   2. If the response is 401, calls POST /api/auth/refresh once (uses the
//      session cookie, no JWT needed). If refresh succeeds, updates
//      localStorage and retries the original request a single time.
//   3. If refresh ALSO returns 401, the original 401 response is returned to
//      the caller — at that point the user genuinely needs to log in again.
//
// Notes:
//   - credentials: 'include' is set so cookies (session) flow on every call.
//   - Caller's headers / Content-Type are preserved. Authorization is
//     overwritten with the current token on each attempt.
//   - Concurrent 401s share the same refresh promise so we don't fire
//     multiple /refresh calls when several requests fail at the same time.

let _refreshPromise = null;

async function refreshToken() {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      const r = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) return null;
      const data = await r.json().catch(() => null);
      if (data?.token) {
        localStorage.setItem('token', data.token);
        return data.token;
      }
      return null;
    } catch {
      return null;
    } finally {
      // Clear the in-flight promise once it resolves (success or fail)
      // so the next 401 can trigger a fresh refresh.
      setTimeout(() => { _refreshPromise = null; }, 0);
    }
  })();
  return _refreshPromise;
}

function buildHeaders(opts, token) {
  const headers = { ...(opts.headers || {}) };
  // Default Content-Type for JSON bodies — caller can override
  if (opts.body && !headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = 'application/json';
  }
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export async function authFetch(url, opts = {}) {
  const token = localStorage.getItem('token');
  let res = await fetch(url, {
    ...opts,
    credentials: 'include',
    headers: buildHeaders(opts, token),
  });

  if (res.status !== 401) return res;

  // 401 — try one refresh, retry once
  const fresh = await refreshToken();
  if (!fresh) return res; // refresh failed; caller gets the original 401

  res = await fetch(url, {
    ...opts,
    credentials: 'include',
    headers: buildHeaders(opts, fresh),
  });
  return res;
}

export default authFetch;
